import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Navbar,
  Button,
  Input,
  TextArea,
  AvatarUpload,
  PhotoGallery,
  SlidePanel,
  FieldRow,
  ToggleRow,
  SectionHeader,
  SaveStatusIndicator,
} from '../components';
import { profileService } from '../services';
import { useUser, useAuth } from '../context';
import {
  GATHERING_TYPES,
  BEHAVIORAL_PROMPTS,
  CONTRIBUTION_TAGS,
  LIKELY_TO_OPTIONS,
  SUBSTANCES_RELATIONSHIP,
  ARRIVAL_TIMES,
  AFTER_PARTY_STYLES,
  RSVP_STYLES,
} from '../constants/partyResumeOptions';

/**
 * Credential Builder Page - Build Your Party Resume
 * Hinge-style profile builder with slide-in panels
 */
export default function CredentialBuilderPage() {
  const navigate = useNavigate();
  const { profile, fetchProfile, walletBalance } = useUser();
  const { logout } = useAuth();

  // Parse JSON fields - handles both string and already-parsed object formats
  const parseJSON = (val, defaultVal = {}) => {
    if (!val) return defaultVal;
    if (typeof val === 'object') return val; // Already parsed
    try {
      return JSON.parse(val);
    } catch {
      return defaultVal;
    }
  };

  const extractInstagramHandle = (socialLink) => {
    if (!socialLink) return '';
    const match = socialLink.match(/instagram\.com\/([^/?]+)/);
    return match ? match[1] : '';
  };

  // Initialize form state from profile
  const [formData, setFormData] = useState({
    handle: profile?.handle || '',
    displayName: profile?.displayName || '',
    vibeCheck: profile?.vibeCheck || '',
    instagramHandle: extractInstagramHandle(profile?.socialLink),
    location: profile?.location || '',
    pronouns: profile?.pronouns || '',
    pronounsVisible: profile?.pronounsVisible ?? true,
    gender: profile?.gender || '',
    drinksAlcohol: profile?.drinksAlcohol || '',
    usesSubstances: profile?.usesSubstances || '',
    acceptTerms: !!profile?.termsAcceptedAt,
    incognitoMode: profile?.incognitoMode ?? false,
    shadowLocation: profile?.shadowLocation ?? false,
    hideFromSearch: profile?.hideFromSearch ?? false,
  });

  // Extended profile fields
  const existingExtended = parseJSON(profile?.extendedProfile, {});
  const [extendedProfile, setExtendedProfile] = useState({
    contributionTags: existingExtended.contributionTags || [],
    signatureContribution: existingExtended.signatureContribution || '',
    likelyTo: existingExtended.likelyTo || [],
    musicThatMovesYou: existingExtended.musicThatMovesYou || '',
    substancesRelationship: existingExtended.substancesRelationship || '',
    lastThreeGatherings: existingExtended.lastThreeGatherings || '',
    vouchReference: existingExtended.vouchReference || '',
    memorableMoment: existingExtended.memorableMoment || '',
    arrivalTime: existingExtended.arrivalTime || '',
    afterPartyStyle: existingExtended.afterPartyStyle || '',
    rsvpStyle: existingExtended.rsvpStyle || '',
    bonusContent: existingExtended.bonusContent || '',
  });

  const [gatheringTypes, setGatheringTypes] = useState(
    parseJSON(profile?.gatheringTypes, [])
  );

  const [behavioralPrompts, setBehavioralPrompts] = useState(
    parseJSON(profile?.behavioralPrompts, {})
  );

  // UI state
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(profile?.avatarUrl || null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activePanel, setActivePanel] = useState(null);
  const [handleStatus, setHandleStatus] = useState({ checking: false, available: null, message: null });

  // Gallery state
  const [galleryPhotos, setGalleryPhotos] = useState(profile?.galleryPhotos || []);
  const [galleryLoading, setGalleryLoading] = useState(false);

  // Track if we've initialized from profile (only sync once on mount)
  const hasInitialized = useRef(false);

  // Sync state when profile loads (only on initial load, not every change)
  useEffect(() => {
    if (profile && !hasInitialized.current) {
      hasInitialized.current = true;

      // Parse JSON strings - handle both string and object formats
      const safeParseJSON = (val, defaultVal) => {
        if (!val) return defaultVal;
        if (typeof val === 'object') return val; // Already parsed
        try {
          return JSON.parse(val);
        } catch {
          return defaultVal;
        }
      };

      const extended = safeParseJSON(profile.extendedProfile, {});

      setFormData({
        handle: profile.handle || '',
        displayName: profile.displayName || '',
        vibeCheck: profile.vibeCheck || '',
        instagramHandle: extractInstagramHandle(profile.socialLink),
        location: profile.location || '',
        pronouns: profile.pronouns || '',
        pronounsVisible: profile.pronounsVisible ?? true,
        gender: profile.gender || '',
        drinksAlcohol: profile.drinksAlcohol || '',
        usesSubstances: profile.usesSubstances || '',
        acceptTerms: !!profile.termsAcceptedAt,
        incognitoMode: profile.incognitoMode ?? false,
        shadowLocation: profile.shadowLocation ?? false,
        hideFromSearch: profile.hideFromSearch ?? false,
      });

      setExtendedProfile({
        contributionTags: extended.contributionTags || [],
        signatureContribution: extended.signatureContribution || '',
        likelyTo: extended.likelyTo || [],
        musicThatMovesYou: extended.musicThatMovesYou || '',
        substancesRelationship: extended.substancesRelationship || '',
        lastThreeGatherings: extended.lastThreeGatherings || '',
        vouchReference: extended.vouchReference || '',
        memorableMoment: extended.memorableMoment || '',
        arrivalTime: extended.arrivalTime || '',
        afterPartyStyle: extended.afterPartyStyle || '',
        rsvpStyle: extended.rsvpStyle || '',
        bonusContent: extended.bonusContent || '',
      });

      setGatheringTypes(safeParseJSON(profile.gatheringTypes, []));
      setBehavioralPrompts(safeParseJSON(profile.behavioralPrompts, {}));
      setAvatarPreview(profile.avatarUrl || null);
      setGalleryPhotos(profile.galleryPhotos || []);
    }
  }, [profile]);

  // Debounced handle check
  useEffect(() => {
    if (!formData.handle || formData.handle.length < 3) {
      setHandleStatus({ checking: false, available: null, message: null });
      return;
    }
    if (formData.handle === profile?.handle) {
      setHandleStatus({ checking: false, available: true, message: null });
      return;
    }

    const timeoutId = setTimeout(async () => {
      setHandleStatus({ checking: true, available: null, message: null });
      const result = await profileService.checkHandle(formData.handle);
      if (result.success) {
        setHandleStatus({
          checking: false,
          available: result.data.available,
          message: result.data.message
        });
      } else {
        setHandleStatus({ checking: false, available: null, message: 'Could not check availability' });
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [formData.handle, profile?.handle]);

  // Handlers
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleExtendedChange = (e) => {
    const { name, value } = e.target;
    setExtendedProfile((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleBehavioralChange = (key, value) => {
    setBehavioralPrompts((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const toggleGatheringType = (type) => {
    setGatheringTypes((prev) => {
      const arr = Array.isArray(prev) ? prev : [];
      return arr.includes(type) ? arr.filter((t) => t !== type) : [...arr, type];
    });
  };

  const toggleContributionTag = (tag) => {
    setExtendedProfile((prev) => {
      const arr = Array.isArray(prev.contributionTags) ? prev.contributionTags : [];
      return {
        ...prev,
        contributionTags: arr.includes(tag) ? arr.filter((t) => t !== tag) : [...arr, tag],
      };
    });
  };

  const toggleLikelyTo = (option) => {
    setExtendedProfile((prev) => {
      const arr = Array.isArray(prev.likelyTo) ? prev.likelyTo : [];
      return {
        ...prev,
        likelyTo: arr.includes(option) ? arr.filter((o) => o !== option) : [...arr, option],
      };
    });
  };

  const handleAvatarChange = (file) => {
    setAvatarFile(file);
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => setAvatarPreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  // Gallery handlers
  const handleGalleryUpload = useCallback(async (file) => {
    setGalleryLoading(true);
    try {
      const result = await profileService.uploadGalleryPhoto(file);
      if (result.success) {
        setGalleryPhotos((prev) => [...prev, result.data]);
      }
    } finally {
      setGalleryLoading(false);
    }
  }, []);

  const handleGalleryDelete = useCallback(async (photoId) => {
    const result = await profileService.deleteGalleryPhoto(photoId);
    if (result.success) {
      setGalleryPhotos((prev) => prev.filter((p) => p.id !== photoId));
    }
  }, []);

  const handleGalleryReorder = useCallback(async (photoIds) => {
    const result = await profileService.reorderGalleryPhotos(photoIds);
    if (result.success && result.data.galleryPhotos) {
      setGalleryPhotos(result.data.galleryPhotos);
    }
  }, []);

  // Auto-save state
  const [autoSaveStatus, setAutoSaveStatus] = useState('idle');
  const autoSaveTimerRef = useRef(null);
  const statusResetRef = useRef(null);
  const isSavingProfileRef = useRef(false);

  // Cleanup timers
  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
      if (statusResetRef.current) clearTimeout(statusResetRef.current);
    };
  }, []);

  const buildProfileData = useCallback(() => ({
    handle: formData.handle || null,
    displayName: formData.displayName,
    vibeCheck: formData.vibeCheck || null,
    socialLink: formData.instagramHandle
      ? `https://instagram.com/${formData.instagramHandle.replace('@', '')}`
      : null,
    location: formData.location || null,
    pronouns: formData.pronouns || null,
    pronounsVisible: formData.pronounsVisible,
    gender: formData.gender || null,
    drinksAlcohol: formData.drinksAlcohol || null,
    usesSubstances: formData.usesSubstances || null,
    gatheringTypes: JSON.stringify(gatheringTypes),
    behavioralPrompts: JSON.stringify(behavioralPrompts),
    extendedProfile: JSON.stringify(extendedProfile),
    acceptTerms: formData.acceptTerms,
    incognitoMode: formData.incognitoMode,
    shadowLocation: formData.shadowLocation,
    hideFromSearch: formData.hideFromSearch,
  }), [formData, gatheringTypes, behavioralPrompts, extendedProfile]);

  const saveProfile = useCallback(async () => {
    if (isSavingProfileRef.current) return;
    if (!formData.displayName.trim()) return;

    isSavingProfileRef.current = true;
    setAutoSaveStatus('saving');

    try {
      const profileData = buildProfileData();
      let result;
      if (profile?.id) {
        result = await profileService.updateProfile(profileData);
      } else {
        result = await profileService.createProfile(profileData);
      }

      if (result.success) {
        setAutoSaveStatus('saved');
        fetchProfile();
      } else {
        setAutoSaveStatus('error');
      }
    } catch {
      setAutoSaveStatus('error');
    } finally {
      isSavingProfileRef.current = false;
      if (statusResetRef.current) clearTimeout(statusResetRef.current);
      statusResetRef.current = setTimeout(() => setAutoSaveStatus('idle'), 3000);
    }
  }, [buildProfileData, profile?.id, formData.displayName, fetchProfile]);

  const debouncedSaveProfile = useCallback(() => {
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => saveProfile(), 500);
  }, [saveProfile]);

  // Auto-save on toggle changes (privacy settings)
  const prevTogglesRef = useRef(null);
  useEffect(() => {
    const toggleState = `${formData.incognitoMode}-${formData.shadowLocation}-${formData.hideFromSearch}`;
    if (prevTogglesRef.current !== null && prevTogglesRef.current !== toggleState) {
      debouncedSaveProfile();
    }
    prevTogglesRef.current = toggleState;
  }, [formData.incognitoMode, formData.shadowLocation, formData.hideFromSearch, debouncedSaveProfile]);

  // Wrap panel close to auto-save
  const closePanelAndSave = useCallback(() => {
    setActivePanel(null);
    saveProfile();
  }, [saveProfile]);

  // Auto-upload avatar immediately on selection
  const handleAvatarAutoUpload = useCallback(async (file) => {
    setAvatarFile(file);
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => setAvatarPreview(e.target.result);
      reader.readAsDataURL(file);

      if (profile?.id) {
        setAutoSaveStatus('saving');
        try {
          const result = await profileService.uploadAvatar(file);
          if (result.success) {
            setAutoSaveStatus('saved');
            fetchProfile();
          } else {
            setAutoSaveStatus('error');
          }
        } catch {
          setAutoSaveStatus('error');
        } finally {
          if (statusResetRef.current) clearTimeout(statusResetRef.current);
          statusResetRef.current = setTimeout(() => setAutoSaveStatus('idle'), 3000);
        }
      }
    }
  }, [profile?.id, fetchProfile]);

  const handleSubmit = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const profileData = {
        handle: formData.handle || null,
        displayName: formData.displayName,
        vibeCheck: formData.vibeCheck || null,
        socialLink: formData.instagramHandle
          ? `https://instagram.com/${formData.instagramHandle.replace('@', '')}`
          : null,
        location: formData.location || null,
        pronouns: formData.pronouns || null,
        pronounsVisible: formData.pronounsVisible,
        gender: formData.gender || null,
        drinksAlcohol: formData.drinksAlcohol || null,
        usesSubstances: formData.usesSubstances || null,
        gatheringTypes: JSON.stringify(gatheringTypes),
        behavioralPrompts: JSON.stringify(behavioralPrompts),
        extendedProfile: JSON.stringify(extendedProfile),
        acceptTerms: formData.acceptTerms,
        incognitoMode: formData.incognitoMode,
        shadowLocation: formData.shadowLocation,
        hideFromSearch: formData.hideFromSearch,
      };

      let result;
      if (profile?.id) {
        result = await profileService.updateProfile(profileData);
      } else {
        result = await profileService.createProfile(profileData);
      }

      if (!result.success) {
        setError(result.error || 'Failed to save profile.');
        setIsLoading(false);
        return;
      }

      if (avatarFile) {
        await profileService.uploadAvatar(avatarFile);
      }

      const updatedProfile = await fetchProfile();
      navigate(updatedProfile?.mode === 'host' ? '/host' : '/guest');
    } catch (err) {
      setError('An unexpected error occurred.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isFormValid =
    formData.displayName.trim().length > 0 &&
    formData.vibeCheck.trim().length > 0 &&
    handleStatus.available !== false;

  // Helper to get display value for multi-select fields
  const getMultiSelectDisplay = (items, max = 3) => {
    if (!Array.isArray(items) || items.length === 0) return null;
    if (items.length <= max) return items.join(', ');
    return `${items.slice(0, max).join(', ')} +${items.length - max}`;
  };

  const getSelectLabel = (value, options) => {
    const opt = options.find((o) => o.value === value);
    return opt?.label || value;
  };

  return (
    <div className="min-h-screen bg-gray-50/50">
      <Navbar user={profile} walletBalance={walletBalance} onLogout={handleLogout} onLogoClick={() => navigate('/')} />

      <main className="max-w-lg mx-auto">
        {/* Header */}
        <div className="p-6 text-center border-b border-gray-200 bg-white">
          <h1 className="font-mono text-xl font-bold uppercase tracking-wider text-black mb-1">
            Party Resume
          </h1>
          <div className="h-4">
            <SaveStatusIndicator status={autoSaveStatus} />
          </div>
        </div>

        {error && (
          <div className="mx-4 mt-4 p-3 border-2 border-red-600 bg-red-50">
            <p className="font-mono text-xs text-red-600">{error}</p>
          </div>
        )}

        {/* Photo Section */}
        <SectionHeader title="Your Photo" subtitle="Faces, not avatars" />
        <div className="p-6 bg-white border-b border-gray-200">
          <div className="flex items-center gap-6">
            <AvatarUpload
              value={avatarPreview}
              onChange={handleAvatarAutoUpload}
              size="lg"
            />
            <div className="flex-1">
              <p className="font-mono text-xs text-gray-500 mb-3">
                Upload a clear photo of yourself
              </p>
              <button
                type="button"
                onClick={() => setActivePanel('gallery')}
                className="font-mono text-xs text-black underline hover:no-underline"
              >
                + Add gallery photos ({galleryPhotos.length}/6)
              </button>
            </div>
          </div>
        </div>

        {/* Identity Section */}
        <SectionHeader title="Identity" />
        <div className="bg-white">
          <FieldRow
            label="Display Name"
            value={formData.displayName}
            placeholder="How should hosts know you?"
            onClick={() => setActivePanel('displayName')}
            required
          />
          <FieldRow
            label="Handle"
            value={formData.handle ? `@${formData.handle}` : null}
            placeholder="@yourhandle"
            onClick={() => setActivePanel('handle')}
          />
          <FieldRow
            label="Vibe Check"
            value={formData.vibeCheck}
            placeholder="What energy do you bring?"
            onClick={() => setActivePanel('vibeCheck')}
            required
          />
        </div>

        {/* The Basics Section */}
        <SectionHeader title="The Basics" />
        <div className="bg-white">
          <FieldRow
            label="Location"
            value={formData.location}
            placeholder="City or area"
            onClick={() => setActivePanel('location')}
          />
          <FieldRow
            label="Pronouns"
            value={formData.pronouns}
            placeholder="e.g., she/her"
            onClick={() => setActivePanel('pronouns')}
            rightElement={
              <span className="font-mono text-xs text-gray-400">
                {formData.pronounsVisible ? 'Visible' : 'Hidden'} &rsaquo;
              </span>
            }
          />
          <FieldRow
            label="Gender"
            value={formData.gender ? formData.gender.charAt(0).toUpperCase() + formData.gender.slice(1) : null}
            onClick={() => setActivePanel('gender')}
          />
          <FieldRow
            label="Drinks Alcohol"
            value={formData.drinksAlcohol ? formData.drinksAlcohol.charAt(0).toUpperCase() + formData.drinksAlcohol.slice(1).replace(/_/g, ' ') : null}
            onClick={() => setActivePanel('drinksAlcohol')}
          />
          <FieldRow
            label="Party Substances"
            value={formData.usesSubstances ? formData.usesSubstances.charAt(0).toUpperCase() + formData.usesSubstances.slice(1).replace(/_/g, ' ') : null}
            onClick={() => setActivePanel('usesSubstances')}
          />
          <FieldRow
            label="Instagram"
            value={formData.instagramHandle ? `@${formData.instagramHandle}` : null}
            onClick={() => setActivePanel('instagram')}
          />
        </div>

        {/* Gathering Types */}
        <SectionHeader title="Gathering Types" subtitle="What excites you?" />
        <div className="bg-white">
          <FieldRow
            label="Types of Gatherings"
            value={getMultiSelectDisplay(gatheringTypes)}
            placeholder="Select what interests you"
            onClick={() => setActivePanel('gatheringTypes')}
          />
        </div>

        {/* What You Bring */}
        <SectionHeader title="What You Bring" />
        <div className="bg-white">
          <FieldRow
            label="Contribution Tags"
            value={getMultiSelectDisplay(extendedProfile.contributionTags)}
            placeholder="What do you bring?"
            onClick={() => setActivePanel('contributions')}
          />
          <FieldRow
            label="Music That Moves You"
            value={extendedProfile.musicThatMovesYou}
            onClick={() => setActivePanel('music')}
          />
          <FieldRow
            label="Arrival Style"
            value={extendedProfile.arrivalTime ? getSelectLabel(extendedProfile.arrivalTime, ARRIVAL_TIMES) : null}
            onClick={() => setActivePanel('arrivalTime')}
          />
        </div>

        {/* Optional Prompts */}
        <SectionHeader title="Optional Prompts" subtitle="Help hosts know you better" />
        <div className="bg-white">
          {BEHAVIORAL_PROMPTS.map((prompt) => (
            <FieldRow
              key={prompt.key}
              label={prompt.label}
              value={behavioralPrompts[prompt.key]}
              placeholder={prompt.placeholder}
              onClick={() => setActivePanel(`prompt_${prompt.key}`)}
            />
          ))}
        </div>

        {/* Privacy Settings */}
        <SectionHeader title="Privacy" />
        <div className="bg-white">
          <ToggleRow
            label="Incognito Mode"
            description="Hide from other guests until greenlit"
            checked={formData.incognitoMode}
            onChange={handleInputChange}
            name="incognitoMode"
          />
          <ToggleRow
            label="Shadow Location"
            description="Show approximate area only"
            checked={formData.shadowLocation}
            onChange={handleInputChange}
            name="shadowLocation"
          />
          <ToggleRow
            label="Hide from Search"
            description="Only findable via direct link"
            checked={formData.hideFromSearch}
            onChange={handleInputChange}
            name="hideFromSearch"
          />
        </div>

        {/* Terms */}
        <div className="p-4 bg-white border-t border-gray-200">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.acceptTerms || false}
              onChange={(e) => setFormData((prev) => ({ ...prev, acceptTerms: e.target.checked }))}
              className="mt-1 w-5 h-5 border-2 border-black accent-black"
            />
            <span className="font-mono text-xs text-gray-600">
              I agree to the Party Covenant and understand my info will be shared with hosts I apply to.
            </span>
          </label>
        </div>

        {/* Save Button */}
        <div className="p-4 bg-white border-t border-gray-200">
          <Button
            type="button"
            variant="primary"
            size="lg"
            fullWidth
            disabled={!isFormValid || isLoading || (!profile?.termsAcceptedAt && !formData.acceptTerms)}
            onClick={handleSubmit}
          >
            {isLoading ? 'SAVING...' : 'SAVE PROFILE'}
          </Button>
        </div>
      </main>

      {/* Slide Panels */}
      <SlidePanel
        isOpen={activePanel === 'gallery'}
        onClose={() => setActivePanel(null)}
        title="Photo Gallery"
      >
        <p className="font-mono text-xs text-gray-500 mb-4">
          Show off your vibe - up to 6 photos
        </p>
        <PhotoGallery
          photos={galleryPhotos}
          onUpload={handleGalleryUpload}
          onDelete={handleGalleryDelete}
          onReorder={handleGalleryReorder}
          maxPhotos={6}
          isLoading={galleryLoading}
        />
      </SlidePanel>

      <SlidePanel
        isOpen={activePanel === 'displayName'}
        onClose={closePanelAndSave}
        title="Display Name"
      >
        <Input
          name="displayName"
          value={formData.displayName}
          onChange={handleInputChange}
          placeholder="How should hosts know you?"
          autoFocus
        />
      </SlidePanel>

      <SlidePanel
        isOpen={activePanel === 'handle'}
        onClose={closePanelAndSave}
        title="Handle"
      >
        <div className="flex items-center mb-4">
          <span className="font-mono text-sm text-gray-500 mr-2">@</span>
          <Input
            name="handle"
            value={formData.handle}
            onChange={handleInputChange}
            placeholder="yourhandle"
            className="flex-1"
            maxLength={50}
            autoFocus
          />
        </div>
        {formData.handle && formData.handle.length >= 3 && (
          <div className="mt-2">
            {handleStatus.checking ? (
              <p className="font-mono text-xs text-gray-500">Checking...</p>
            ) : handleStatus.available === true ? (
              <p className="font-mono text-xs text-green-600">Available</p>
            ) : handleStatus.available === false ? (
              <p className="font-mono text-xs text-red-600">{handleStatus.message || 'Taken'}</p>
            ) : null}
          </div>
        )}
      </SlidePanel>

      <SlidePanel
        isOpen={activePanel === 'vibeCheck'}
        onClose={closePanelAndSave}
        title="Vibe Check"
      >
        <p className="font-mono text-xs text-gray-500 mb-4">
          What do you bring to the party?
        </p>
        <TextArea
          name="vibeCheck"
          value={formData.vibeCheck}
          onChange={handleInputChange}
          placeholder="Tell hosts about yourself. What energy do you bring?"
          rows={6}
          maxLength={1000}
          autoFocus
        />
      </SlidePanel>

      <SlidePanel
        isOpen={activePanel === 'location'}
        onClose={closePanelAndSave}
        title="Location"
      >
        <Input
          name="location"
          value={formData.location}
          onChange={handleInputChange}
          placeholder="City or area"
          autoFocus
        />
      </SlidePanel>

      <SlidePanel
        isOpen={activePanel === 'pronouns'}
        onClose={closePanelAndSave}
        title="Pronouns"
      >
        <Input
          name="pronouns"
          value={formData.pronouns}
          onChange={handleInputChange}
          placeholder="e.g., she/her, he/him, they/them"
          className="mb-4"
          autoFocus
        />
        <ToggleRow
          label="Visible on Profile"
          checked={formData.pronounsVisible}
          onChange={handleInputChange}
          name="pronounsVisible"
        />
      </SlidePanel>

      <SlidePanel
        isOpen={activePanel === 'gender'}
        onClose={closePanelAndSave}
        title="Gender"
      >
        <div className="space-y-2">
          {[
            { value: 'man', label: 'Man' },
            { value: 'woman', label: 'Woman' },
            { value: 'non-binary', label: 'Non-binary' },
          ].map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => {
                setFormData((prev) => ({ ...prev, gender: opt.value }));
                closePanelAndSave();
              }}
              className={`
                w-full p-4 text-left border-2 transition-colors
                ${formData.gender === opt.value
                  ? 'border-black bg-black text-white'
                  : 'border-gray-200 hover:border-black'
                }
              `}
            >
              <span className="font-mono text-sm">{opt.label}</span>
            </button>
          ))}
        </div>
      </SlidePanel>

      <SlidePanel
        isOpen={activePanel === 'drinksAlcohol'}
        onClose={closePanelAndSave}
        title="Do You Drink Alcohol?"
      >
        <div className="space-y-2">
          {[
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' },
            { value: 'prefer_not_to_say', label: 'Prefer not to say' },
          ].map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => {
                setFormData((prev) => ({ ...prev, drinksAlcohol: opt.value }));
                closePanelAndSave();
              }}
              className={`
                w-full p-4 text-left border-2 transition-colors
                ${formData.drinksAlcohol === opt.value
                  ? 'border-black bg-black text-white'
                  : 'border-gray-200 hover:border-black'
                }
              `}
            >
              <span className="font-mono text-sm">{opt.label}</span>
            </button>
          ))}
        </div>
      </SlidePanel>

      <SlidePanel
        isOpen={activePanel === 'usesSubstances'}
        onClose={closePanelAndSave}
        title="Party Substances"
      >
        <div className="space-y-2">
          {[
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' },
            { value: 'prefer_not_to_say', label: 'Prefer not to say' },
          ].map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => {
                setFormData((prev) => ({ ...prev, usesSubstances: opt.value }));
                closePanelAndSave();
              }}
              className={`
                w-full p-4 text-left border-2 transition-colors
                ${formData.usesSubstances === opt.value
                  ? 'border-black bg-black text-white'
                  : 'border-gray-200 hover:border-black'
                }
              `}
            >
              <span className="font-mono text-sm">{opt.label}</span>
            </button>
          ))}
        </div>
      </SlidePanel>

      <SlidePanel
        isOpen={activePanel === 'instagram'}
        onClose={closePanelAndSave}
        title="Instagram"
      >
        <div className="flex items-center">
          <span className="font-mono text-sm text-gray-500 mr-2">@</span>
          <Input
            name="instagramHandle"
            value={formData.instagramHandle}
            onChange={handleInputChange}
            placeholder="yourhandle"
            className="flex-1"
            autoFocus
          />
        </div>
      </SlidePanel>

      <SlidePanel
        isOpen={activePanel === 'gatheringTypes'}
        onClose={closePanelAndSave}
        title="Gathering Types"
      >
        <p className="font-mono text-xs text-gray-500 mb-4">
          Select all that excite you
        </p>
        <div className="space-y-6">
          {Object.entries(GATHERING_TYPES).map(([key, category]) => (
            <div key={key}>
              <h3 className="font-mono text-xs font-bold uppercase text-gray-700 mb-3">
                {category.label}
              </h3>
              <div className="flex flex-wrap gap-2">
                {category.options.map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => toggleGatheringType(option)}
                    className={`
                      px-3 py-2 font-mono text-xs uppercase border-2 transition-colors
                      ${Array.isArray(gatheringTypes) && gatheringTypes.includes(option)
                        ? 'bg-black text-white border-black'
                        : 'bg-white text-black border-gray-300 hover:border-black'
                      }
                    `}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </SlidePanel>

      <SlidePanel
        isOpen={activePanel === 'contributions'}
        onClose={closePanelAndSave}
        title="What You Bring"
      >
        <p className="font-mono text-xs text-gray-500 mb-4">
          Beyond your presence, what do you bring?
        </p>
        <div className="flex flex-wrap gap-2 mb-6">
          {CONTRIBUTION_TAGS.map((tag) => (
            <button
              key={tag}
              type="button"
              onClick={() => toggleContributionTag(tag)}
              className={`
                px-3 py-2 font-mono text-xs uppercase border-2 transition-colors
                ${Array.isArray(extendedProfile.contributionTags) && extendedProfile.contributionTags.includes(tag)
                  ? 'bg-black text-white border-black'
                  : 'bg-white text-black border-gray-300 hover:border-black'
                }
              `}
            >
              {tag}
            </button>
          ))}
        </div>

        <div className="mb-4">
          <label className="block font-mono text-xs font-bold uppercase text-gray-700 mb-2">
            Signature Contribution
          </label>
          <Input
            name="signatureContribution"
            value={extendedProfile.signatureContribution}
            onChange={handleExtendedChange}
            placeholder="Your signature move..."
          />
        </div>

        <div>
          <label className="block font-mono text-xs font-bold uppercase text-gray-700 mb-3">
            More likely to...
          </label>
          <div className="flex flex-wrap gap-2">
            {LIKELY_TO_OPTIONS.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => toggleLikelyTo(option)}
                className={`
                  px-3 py-2 font-mono text-xs uppercase border-2 transition-colors
                  ${Array.isArray(extendedProfile.likelyTo) && extendedProfile.likelyTo.includes(option)
                    ? 'bg-black text-white border-black'
                    : 'bg-white text-black border-gray-300 hover:border-black'
                  }
                `}
              >
                {option}
              </button>
            ))}
          </div>
        </div>
      </SlidePanel>

      <SlidePanel
        isOpen={activePanel === 'music'}
        onClose={closePanelAndSave}
        title="Music That Moves You"
      >
        <Input
          name="musicThatMovesYou"
          value={extendedProfile.musicThatMovesYou}
          onChange={handleExtendedChange}
          placeholder="Genres, artists, vibes..."
          autoFocus
        />
      </SlidePanel>

      <SlidePanel
        isOpen={activePanel === 'arrivalTime'}
        onClose={closePanelAndSave}
        title="Arrival Style"
      >
        <div className="space-y-2">
          {ARRIVAL_TIMES.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => {
                setExtendedProfile((prev) => ({ ...prev, arrivalTime: opt.value }));
                closePanelAndSave();
              }}
              className={`
                w-full p-4 text-left border-2 transition-colors
                ${extendedProfile.arrivalTime === opt.value
                  ? 'border-black bg-black text-white'
                  : 'border-gray-200 hover:border-black'
                }
              `}
            >
              <span className="font-mono text-sm">{opt.label}</span>
            </button>
          ))}
        </div>
      </SlidePanel>

      {/* Behavioral Prompt Panels */}
      {BEHAVIORAL_PROMPTS.map((prompt) => (
        <SlidePanel
          key={prompt.key}
          isOpen={activePanel === `prompt_${prompt.key}`}
          onClose={closePanelAndSave}
          title={prompt.label}
        >
          <TextArea
            value={behavioralPrompts[prompt.key] || ''}
            onChange={(e) => handleBehavioralChange(prompt.key, e.target.value)}
            placeholder={prompt.placeholder}
            rows={4}
            maxLength={500}
            autoFocus
          />
        </SlidePanel>
      ))}
    </div>
  );
}
